import React from "react";

/**
 * AppLogo (JiraniLogo v3)
 * Not clickable: no link, no navigate, no onClick, no pointer cursor.
 *
 * Props
 *   height    number   Rendered height in px. Default 40.
 *   theme     string   "onLight" (green icon, black wordmark) or
 *                      "onDark" (white icon + wordmark). Default "onLight".
 *   iconOnly  boolean  Icon without wordmark. Default false.
 *   className / style  Passed to the wrapping <span>.
 * Any other props are ignored.
 */

// Load Instrument Serif once so the wordmark renders on every page.
if (typeof document !== "undefined" && !document.getElementById("jf-logo-font")) {
  const link = document.createElement("link");
  link.id = "jf-logo-font";
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&display=swap";
  document.head.appendChild(link);
}

const THEMES = {
  onLight: {
    circleLight: "#8FD4B0",
    circleDark: "#1E9254",
    houseStroke: "#1E9254",
    text: "#0A0A0A",
  },
  onDark: {
    circleLight: "rgba(255,255,255,0.55)",
    circleDark: "#FFFFFF",
    houseStroke: "#FFFFFF",
    text: "#FFFFFF",
  },
};

const FALLBACK_WORD_W = 190; // used only until the real width is measured

export default function AppLogo({
  height = 40,
  theme = "onLight",
  iconOnly = false,
  className = "",
  style = {},
}) {
  const palette = THEMES[theme] || THEMES.onLight;
  const textRef = React.useRef(null);
  const [measuredWordW, setMeasuredWordW] = React.useState(null);

  const iconW = 100;
  const gap = 16;
  const fontSize = 58;
  const baselineY = 74;
  const wordW = iconOnly ? 0 : measuredWordW ?? FALLBACK_WORD_W;
  const totalW = iconOnly ? iconW : iconW + gap + wordW;
  const totalH = 100;

  const fontStack = "'Instrument Serif', 'Times New Roman', Georgia, serif";

  React.useEffect(() => {
    if (iconOnly) return;
    const measure = () => {
      if (!textRef.current) return;
      try {
        const bbox = textRef.current.getBBox();
        if (bbox && bbox.width > 0) setMeasuredWordW(bbox.width);
      } catch {
        /* not painted yet, the timeout below retries */
      }
    };
    if (typeof document !== "undefined" && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure);
    } else {
      measure();
    }
    const t = setTimeout(measure, 150);
    return () => clearTimeout(t);
  }, [iconOnly]);

  return (
    <span
      className={`jirani-logo ${className}`.trim()}
      style={{ display: "inline-block", lineHeight: 0, cursor: "default", ...style }}
    >
      <svg
        height={height}
        width={(height * totalW) / totalH}
        viewBox={`0 0 ${totalW} ${totalH}`}
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label={iconOnly ? "Jirani" : "Jirani logo"}
      >
        <title>Jirani</title>

        <g
          fill="none"
          stroke={palette.houseStroke}
          strokeWidth="4.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 44 L50 12 L88 44" />
          <path d="M20 40 V80 Q20 84 24 84 H76 Q80 84 80 80 V40" />
        </g>
        <circle cx="42" cy="62" r="16" fill={palette.circleLight} />
        <circle cx="58" cy="62" r="16" fill={palette.circleDark} />

        {!iconOnly && (
          <text
            ref={textRef}
            x={iconW + gap}
            y={baselineY}
            fontFamily={fontStack}
            fontStyle="italic"
            fontWeight="400"
            fontSize={fontSize}
            fill={palette.text}
          >
            Jirani
          </text>
        )}
      </svg>
    </span>
  );
}

/**
 * useNavTheme — "onDark" until scrolled past `threshold`, then "onLight".
 */
export function useNavTheme(threshold = 80) {
  const [theme, setTheme] = React.useState("onDark");

  React.useEffect(() => {
    const onScroll = () => {
      setTheme(window.scrollY > threshold ? "onLight" : "onDark");
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  return theme;
}